"use client"

import { ZoomEffectButton } from "@/(admin)/components/button/ZoomEffectButton"
import "@/(admin)/modules/ui-builder/config/themes/tamda-one/Career/styles.css"
import { FAQsContent } from "@/(admin)/modules/ui-builder/config/themes/tamda-one/FAQs/RenderConfig"
import ServiceDetail from "@/(admin)/modules/ui-builder/config/themes/tamda-one/ServiceSidebar/components/ServiceDetail"
import { ServiceSidebarNavItem } from "@/(admin)/modules/ui-builder/config/themes/tamda-one/ServiceSidebar/components/ServiceSidebarNavItem"
import { ServiceSidebarSkeleton } from "@/(admin)/modules/ui-builder/config/themes/tamda-one/ServiceSidebar/components/ServiceSidebarSkeleton"
import { Images } from "@/constants/images"
import { useServiceSidebar } from "@/hook/useServiceSidebar"
import { t } from "@/languages/i18n"
import { CareerJobDetailItem, ServiceSidebarContentProps } from "@/types/types"
import Image from "next/image"
import React, { useMemo, useState } from "react"
import { twMerge } from "tailwind-merge"

export const ServiceSidebarContent: React.FC<ServiceSidebarContentProps> = ({
  id,
  className,
  titleCategory,
  category,
  responsiveCSS,
  puck,
}) => {
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0)
  const activeCategory = category[activeCategoryIndex]
  const locale = typeof document !== "undefined" ? document.documentElement?.lang || "vi" : "vi"

  // Memoize active category contentType
  const activeContentType = useMemo(() => {
    return category[activeCategoryIndex]?.contentType
  }, [category, activeCategoryIndex])

  const {
    careers,
    loading: careersLoading,
    error: careersError,
    expandedMap,
    setExpandedMap,
  } = useServiceSidebar({
    activeContentType,
    locale,
  })

  // Job detail items configuration
  const jobDetailItems: CareerJobDetailItem[] = [
    {
      icon: Images.iconJobCareer,
      labelKey: "Job Type",
      valueKey: "jobType",
      alt: "icon job",
    },
    {
      icon: Images.iconSalaryCareer,
      labelKey: "Salary",
      valueKey: "salary",
      alt: "icon salary",
    },
    {
      icon: Images.iconCalendarCareer,
      labelKey: "Exp date",
      valueKey: "expiredDate",
      alt: "icon expired date",
    },
    {
      icon: Images.iconJobCareer,
      labelKey: "Quantity",
      valueKey: "quantity",
      alt: "icon quantity",
    },
  ]

  const renderContent = () => {
    if (!activeCategory) {
      return null
    }

    const contentType = activeCategory.contentType

    switch (contentType) {
      case "faqs": {
        const faqsData = activeCategory?.faqs as Array<{ question: string; answer: string }> | undefined

        if (!faqsData || faqsData.length === 0) {
          return <div className="py-16 text-center text-gray-500 text-lg">Chưa có câu hỏi thường gặp nào.</div>
        }

        return (
          <FAQsContent
            id={`faqs-sidebar-${activeCategoryIndex}`}
            className=""
            faqs={faqsData}
            responsiveCSS={responsiveCSS}
          />
        )
      }

      case "ckeditor": {
        if (activeCategory.contentCkeditor) {
          return (
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: activeCategory.contentCkeditor }} />
          )
        }
        return null
      }

      case "career": {
        if (careersLoading) {
          return <ServiceSidebarSkeleton />
        }

        if (careersError) {
          return (
            <div className="text-red-500">
              {t("Error loading careers")}: {careersError}
            </div>
          )
        }

        if (!careers || careers.length === 0) {
          return <div className="text-gray-500">{t("No careers available")}</div>
        }

        return (
          <div className="flex flex-col border-t border-[rgba(0, 0, 0, 0.9)]">
            {careers.map(career => (
              <div key={career.id} className="mt-5 pb-5 border-b border-[rgba(0, 0, 0, 0.9)]">
                <div className="grid grid-cols-1 sm:grid-cols-12">
                  {/* Left Side - Job Details */}
                  <div className="col-span-5 sm:pr-[0.9375rem] flex flex-col sm:gap-[0.9375rem] gap-[0.3125rem]">
                    <h5 className="mb-[0.625rem] sm:mb-0 text-2xl leading-[1.625rem] font-black text-[#212529]">
                      {career?.attributes?.position}
                    </h5>
                    {jobDetailItems.map((item, index) => {
                      const value = career?.attributes?.[item.valueKey as keyof typeof career.attributes] || ""
                      let displayText = ""

                      if (item.labelKey === "Salary:") {
                        displayText = `${t("Salary:")}${value}`
                      } else if (item.labelKey === "Exp date:") {
                        displayText = `${t("Exp date:")} ${value}`
                      } else {
                        displayText = `${t(item.labelKey)}: ${value}`
                      }

                      return (
                        <div key={index} className="flex items-center gap-[0.625rem]">
                          <div className="relative w-4 h-4">
                            <Image src={item.icon} alt={item.alt} fill className="object-contain" />
                          </div>
                          <span className="text-base text-[#333333]">{displayText}</span>
                        </div>
                      )
                    })}
                  </div>

                  {/* Right Side - Company Info */}
                  <div className="col-span-5 sm:px-[0.9375rem] mt-5 sm:mt-0 flex gap-[1.875rem] items-center">
                    <div className="flex items-center w-full h-full max-h-[6.25rem] max-w-[6.25rem] p-[0.125rem] rounded-lg border border-[#e4e4e7]">
                      <div className="relative w-[5.125rem] h-[3.5625rem]">
                        <Image src={Images.brandCareer} alt="brand" fill className="object-contain" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-[0.9375rem]">
                      <h6 className="text-xl leading-[1.375rem] text-[#212529] font-bold">
                        {career?.attributes?.company}
                      </h6>
                      <div className="flex items-center gap-[0.625rem]">
                        <div className="relative w-4 h-4">
                          <Image src={Images.iconLocationCareer} alt="icon location" fill className="object-contain" />
                        </div>
                        <span className="text-base text-[#333333]">
                          {t("Location: ")} {career?.attributes?.branch || career?.attributes?.location}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Button Section */}
                  <div className="col-span-2 sm:pl-[0.9375rem] mt-5 sm:mt-0 flex items-center">
                    <div
                      style={{
                        width: "8.40625rem",
                        height: "2.25rem",
                      }}
                      className="[&>div>button]:!w-[8.40625rem] [&>div>button]:!h-[2.25rem] [&>div>button>span]:!w-[10.0875rem] [&>div>button>span]:!h-[2.7rem]"
                    >
                      <ZoomEffectButton
                        text={t("Apply now")}
                        onClick={() =>
                          setExpandedMap(prev => ({
                            ...prev,
                            [career?.id?.toString() || ""]: !prev[career?.id?.toString() || ""],
                          }))
                        }
                        isInline={false}
                      />
                    </div>
                  </div>
                </div>
                {expandedMap[career?.id?.toString() || ""] && (
                  <ServiceDetail data={career} locale={locale} phoneNumber={career?.attributes?.phoneNumber} />
                )}
              </div>
            ))}
          </div>
        )
      }

      case "link": {
        // For link content type, navigation is handled by ServiceSidebarNavItem
        // No content to display, just return null
        return null
      }

      default:
        return null
    }
  }

  return (
    <>
      {responsiveCSS}
      <div className={twMerge(className)} id={id}>
        <div className="bg-[#FFF]">
          <div className="sm:mx-auto mt-[4.3125rem] mb-[6.875rem]  max-w-[1320px] px-4 sm:px-0 flex flex-col sm:flex-row flex-wrap sm:gap-0 gap-8 justify-start">
            {/* Left Sidebar - Navigation */}
            <div className="w-full sm:w-[29.1667%]">
              <h2 className="sm:text-xl text-sm font-bold sm:leading-[1.9375rem] text-[#333333] mb-5 sm:mb-4 sm:pl-[1.3125rem]">
                {titleCategory}
              </h2>
              <>
                {responsiveCSS}
                <div className={twMerge(className)} id={id}>
                  <div className="bg-[#FFF]">
                    <div className="sm:mx-auto sm:mt-[4.3125rem] mt-0 sm:mb-[6.875rem] mb-0 max-w-[82.5rem] px-0 sm:px-4 flex flex-col sm:flex-row flex-wrap sm:gap-0 gap-8 justify-between">
                      <div className="w-full">
                        <nav className="flex flex-wrap justify-center sm:flex-col sm:gap-0 gap-2 sm:space-y-0">
                          {category.map((item, index) => (
                            <ServiceSidebarNavItem
                              key={index}
                              categoryName={item.categoryName}
                              link={item.link}
                              isActive={activeCategoryIndex === index}
                              onClick={() => setActiveCategoryIndex(index)}
                              contentType={item.contentType}
                            />
                          ))}
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            </div>

            {/* Right Content - Conditional rendering based on contentType */}
            <div className="w-full sm:pl-10 pl-[0.625rem] sm:w-[70.8333%]">
              {activeCategory && activeCategory.contentType !== "link" && (
                <div className="mb-6">
                  <h3 className="text-xl font-bold leading-[1.9375rem] text-[#333333] mb-4">
                    {activeCategory.categoryName}
                  </h3>
                </div>
              )}
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
